class WProductBuyer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    const decoder = document.createElement('textarea');
    decoder.innerHTML = this.dataset.moneyFormat || '{{amount}}€';
    this.moneyFormat = decoder.value;
    this.form = this.querySelector('.w-pb-form');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.planInput = this.querySelector('[data-plan-input]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.qtySelect = this.querySelector('.w-pb-qty__select');
    this.tiers = Array.from(this.querySelectorAll('.w-pb-tier'));
    this.freqOptions = Array.from(this.querySelectorAll('.w-pb-freq__option'));
    this.activeTier = this.tiers.find((t) => t.classList.contains('is-active')) || this.tiers[0];
    this.activeFreq =
      this.freqOptions.find((f) => f.classList.contains('is-active')) || this.freqOptions[0];
    this.tiers.forEach((tier) =>
      tier.addEventListener('click', () => this.selectTier(tier), { signal }),
    );
    this.freqOptions.forEach((opt) => {
      opt.addEventListener('click', () => this.selectFreq(opt), { signal });
      opt.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.selectFreq(opt);
          }
        },
        { signal },
      );
    });
    this.qtySelect?.addEventListener('change', () => this.render(), { signal });
    this.initGallery(signal);
    this.initFaq(signal);
    this.initDelivery();
    this.render();
  }
  disconnectedCallback() {
    this.controller?.abort();
  }
  formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const number = Number(cents);
    if (Number.isNaN(number)) return '';
    const withComma = (num, precision = 2, thousands = '.', decimal = ',') => {
      const fixed = (num / 100).toFixed(precision);
      const parts = fixed.split('.');
      const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision ? int + decimal + parts[1] : int;
    };
    switch ((this.moneyFormat.match(placeholder) || [])[1]) {
      case 'amount':
        value = withComma(number, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withComma(number, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withComma(number, 2, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withComma(number, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withComma(number, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withComma(number, 0, ' ', '');
        break;
      default:
        value = withComma(number, 2, '.', ',');
    }
    return this.moneyFormat.replace(placeholder, value);
  }
  stripZeroCents(text) {
    return text.replace(/([.,])00(\D|$)/, '$2');
  }
  price(el, cents) {
    if (el) el.textContent = this.stripZeroCents(this.formatMoney(cents));
  }
  selectTier(tier) {
    this.tiers.forEach((t) => t.classList.toggle('is-active', t === tier));
    this.activeTier = tier;
    this.render();
  }
  selectFreq(opt) {
    this.freqOptions.forEach((f) => f.classList.toggle('is-active', f === opt));
    this.activeFreq = opt;
    this.render();
  }
  q(freq, key) {
    return this.querySelector(`[data-freq="${freq}"] [data-price-${key}]`);
  }
  perDay(cents, days) {
    return this.stripZeroCents(this.formatMoney(Math.round(cents / days))) + ' /jour';
  }
  render() {
    const tier = this.activeTier;
    if (!tier) return;
    const qty = this.qtySelect ? parseInt(this.qtySelect.value, 10) || 1 : 1;
    const addQty = parseInt(tier.dataset.addQty, 10) || 1;
    const months = parseInt(tier.dataset.months, 10) || 1;
    const priceOnce = parseInt(tier.dataset.price, 10) || 0;
    const priceSub = tier.dataset.priceSub ? parseInt(tier.dataset.priceSub, 10) : null;
    const isSub = this.activeFreq && this.activeFreq.dataset.freq === 'subscription';
    const days = 30 * months;
    if (this.variantInput) this.variantInput.value = tier.dataset.variantId || '';
    if (this.qtyInput) this.qtyInput.value = addQty * qty;
    if (this.planInput)
      this.planInput.value = isSub && priceSub != null ? tier.dataset.sellingPlanId || '' : '';
    const noteEl = this.querySelector('[data-step-note]');
    if (noteEl && tier.dataset.note) noteEl.textContent = tier.dataset.note;
    this.price(this.q('onetime', 'value'), priceOnce);
    const onceDay = this.q('onetime', 'day');
    if (onceDay) onceDay.textContent = this.perDay(priceOnce, days);
    const subValue = this.q('subscription', 'value');
    if (subValue) {
      const subCompare = this.q('subscription', 'compare');
      const subDay = this.q('subscription', 'day');
      const subSave = this.q('subscription', 'save');
      if (priceSub != null) {
        const saved = priceOnce - priceSub;
        if (subCompare)
          subCompare.textContent =
            saved > 0 ? this.stripZeroCents(this.formatMoney(priceOnce)) : '';
        this.price(subValue, priceSub);
        if (subSave)
          subSave.textContent = saved > 0 ? this.stripZeroCents(this.formatMoney(saved)) : '';
        if (subDay) subDay.textContent = this.perDay(priceSub, days);
      } else {
        if (subCompare) subCompare.textContent = '';
        if (subSave) subSave.textContent = '';
        this.price(subValue, priceOnce);
        if (subDay) subDay.textContent = this.perDay(priceOnce, days);
      }
    }
    this.tiers.forEach((t) => {
      const priceEl = t.querySelector('.w-pb-tier__price');
      if (!priceEl) return;
      const tMonths = parseInt(t.dataset.months, 10) || 1;
      const tOnce = parseInt(t.dataset.price, 10) || 0;
      const tSub = t.dataset.priceSub ? parseInt(t.dataset.priceSub, 10) : null;
      const perSachet = Math.round((isSub && tSub != null ? tSub : tOnce) / tMonths);
      priceEl.textContent = this.stripZeroCents(this.formatMoney(perSachet)) + ' /sachet';
    });
    const total = (isSub && priceSub != null ? priceSub : priceOnce) * qty;
    this.price(this.querySelector('[data-cta-price]'), total);
  }
  getStepWidth() {
    // Largeur réelle d'un slide + l'espace (gap CSS) qui le sépare du suivant.
    // Mesuré dynamiquement plutôt que supposé à 100%, pour rester juste même
    // si la fenêtre est redimensionnée ou si le gap change côté CSS.
    const styles = getComputedStyle(this.slidesEl);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const slideWidth = this.slides[0]?.getBoundingClientRect().width || 0;
    return slideWidth + gap;
  }
  initGallery(signal) {
    this.slidesEl = this.querySelector('.w-pb-gallery__slides');
    this.slides = Array.from(this.querySelectorAll('.w-pb-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.w-pb-gallery__thumb'));
    this.galleryIndex = 0;
    this.galleryCount = this.slides.length;
    if (this.galleryCount <= 1) return;
    this.prevBtn = this.querySelector('.w-pb-gallery__arrow--prev');
    this.nextBtn = this.querySelector('.w-pb-gallery__arrow--next');
    this.prevBtn?.addEventListener('click', () => this.goTo(this.galleryIndex - 1), { signal });
    this.nextBtn?.addEventListener('click', () => this.goTo(this.galleryIndex + 1), { signal });
    this.thumbs.forEach((thumb, i) =>
      thumb.addEventListener('click', () => this.goTo(i), { signal }),
    );
    // --- Glissement tactile qui suit le doigt en temps réel, avec verrouillage
    // de direction pour empêcher la page de scroller verticalement pendant
    // qu'on glisse horizontalement (et inversement). ---
    const viewport = this.querySelector('.w-pb-gallery__main');
    let startX = null;
    let startY = null;
    let currentX = null;
    let dragging = false;
    let stepWidth = 0;
    let directionLocked = null; // null | 'horizontal' | 'vertical'
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      currentX = startX;
      dragging = true;
      directionLocked = null;
      stepWidth = this.getStepWidth();
      // Désactive la transition CSS pendant le glissement, pour un suivi 1:1 sans latence.
      if (this.slidesEl) this.slidesEl.style.transition = 'none';
    };
    const onTouchMove = (e) => {
      if (!dragging) return;
      currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      // Dès qu'un mouvement significatif est détecté, on fige la direction
      // dominante pour tout le reste du geste (évite les hésitations).
      if (directionLocked === null && (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6)) {
        directionLocked = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      }
      if (directionLocked === 'vertical') {
        // L'utilisateur scrolle la page : on abandonne le geste horizontal
        // et on laisse le navigateur gérer le scroll normalement.
        dragging = false;
        if (this.slidesEl) this.slidesEl.style.transition = '';
        this.updateGallery();
        return;
      }
      if (directionLocked === 'horizontal') {
        // Empêche la page de scroller verticalement pendant ce geste.
        e.preventDefault();
      }
      const basePx = -this.galleryIndex * stepWidth;
      if (this.slidesEl) {
        this.slidesEl.style.transform = `translateX(${basePx + deltaX}px)`;
      }
    };
    const onTouchEnd = () => {
      if (!dragging) return;
      dragging = false;
      const delta = currentX - startX;
      startX = null;
      startY = null;
      currentX = null;
      directionLocked = null;
      // Réactive la transition pour l'animation de retour/passage à l'image suivante.
      if (this.slidesEl) this.slidesEl.style.transition = '';
      if (Math.abs(delta) > 40) {
        this.goTo(this.galleryIndex + (delta < 0 ? 1 : -1));
      } else {
        // Glissement trop court : on revient proprement sur l'image actuelle.
        this.updateGallery();
      }
    };
    viewport?.addEventListener('touchstart', onTouchStart, { passive: true, signal });
    viewport?.addEventListener('touchmove', onTouchMove, { passive: false, signal });
    viewport?.addEventListener('touchend', onTouchEnd, { passive: true, signal });
    viewport?.addEventListener('touchcancel', onTouchEnd, { passive: true, signal });
    // État initial : on positionne la galerie et on synchronise les miniatures,
    // mais SANS faire défiler la page vers la bande de miniatures — elles
    // sont probablement hors écran au premier chargement, et scrollIntoView()
    // forcerait un saut de page indésirable au chargement, même avec
    // block: 'nearest'.
    this.updateGallery(false);
  }
  goTo(index) {
    this.galleryIndex = Math.max(0, Math.min(index, this.galleryCount - 1));
    this.updateGallery();
  }
  updateGallery(scrollThumbIntoView = true) {
    if (this.slidesEl) {
      const stepWidth = this.getStepWidth();
      this.slidesEl.style.transform = `translateX(${-this.galleryIndex * stepWidth}px)`;
    }
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.galleryIndex));
    // Fait défiler la bande de miniatures pour garder la miniature active
    // visible, uniquement lors d'une vraie interaction (clic, glissement) —
    // jamais lors de la mise en place initiale de la galerie au chargement.
    if (scrollThumbIntoView) {
      const activeThumb = this.thumbs[this.galleryIndex];
      activeThumb?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    if (this.prevBtn) this.prevBtn.disabled = this.galleryIndex <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.galleryIndex >= this.galleryCount - 1;
  }
  initFaq(signal) {
    const list = this.querySelector('.w-pb-faq');
    list?.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('.w-pb-faq__q');
        if (!trigger || !list.contains(trigger)) return;
        const open = trigger.getAttribute('aria-expanded') !== 'true';
        trigger.setAttribute('aria-expanded', String(open));
        trigger.closest('.w-pb-faq__item')?.classList.toggle('is-open', open);
      },
      { signal },
    );
  }
  initDelivery() {
    const el = this.querySelector('[data-delivery]');
    if (!el || this.dataset.deliveryDynamic !== 'true') return;
    const min = parseInt(this.dataset.deliveryMin, 10) || 2;
    const max = parseInt(this.dataset.deliveryMax, 10) || 4;
    const prefix = el.dataset.deliveryPrefix || 'Livré entre :';
    const orderNote = 'pour toute commande passée avant minuit.';
    const countSaturday = this.dataset.deliverySaturday !== 'false';
    const fmt = new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    const addBusinessDays = (n) => {
      const d = new Date();
      let added = 0;
      while (added < n) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day === 0) continue;
        if (day === 6 && !countSaturday) continue;
        added += 1;
      }
      return d;
    };
    let html = `${prefix} <span class="w-pb-stock__date">${fmt.format(addBusinessDays(min))}</span> et <span class="w-pb-stock__date">${fmt.format(addBusinessDays(max))}</span>`;
    if (orderNote) html += ` <span class="w-pb-stock__note">${orderNote}</span>`;
    el.innerHTML = html;
  }
}
customElements.define('w-product-buyer', WProductBuyer);